import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './index.scss';
import {
  message,
  Upload,
  Spin,
  Alert,
  Table,
  Pagination,
  Button,
  Modal,
  Form,
  Select,
  Space,
  Progress,
  Tabs,
  Tag,
  Collapse,
  Checkbox,
  Input,
  Empty,
} from 'antd';
import type { TableProps, PaginationProps, TabsProps } from 'antd';
import File from '@/assets/cloudUpload.svg';
import type { UploadFile, RcFile, UploadChangeParam } from 'antd/es/upload/interface';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
  getFilehubPresignedURL,
  confirmUploadFile,
  deleteAdvisorFile,
  getSharedFileDownloadLink,
  getFilehubPresignedURLResponse,
  FilehubFilesData,
  getAdvisorFilesWithShares,
  getReceivedAdvisorFilesWithShares,
  getReceivedAdvisorFilesWithSharesByClientId,
  InvestorProfile,
  getInvestors,
} from '@/api/custom';
import {
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatFileSize } from '@wavvest/shared-utils';

interface UploadFormValues {
  is_public: boolean;
  requires_document_processing: boolean;
  shares: string[];
}

interface EnhancedFilehubFilesData extends FilehubFilesData {
  source: 'sent' | 'received';
}

const MAX_UPLOAD_FILE_SIZE_BYTES = 100 * 1024 * 1024;

const FILE_TYPES_BY_EXTENSION: Record<string, string> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const getFileType = (file: RcFile): string => {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension
    ? FILE_TYPES_BY_EXTENSION[extension] || 'application/octet-stream'
    : 'application/octet-stream';
};

const isFileWithinSizeLimit = (file: RcFile) => file.size <= MAX_UPLOAD_FILE_SIZE_BYTES;

const isPresentString = (value: string | null | undefined): value is string => Boolean(value);

const FileHub: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [activeTab, setActiveTab] = useState<string>('all');

  const [sentPage, setSentPage] = useState(1);
  const [sentPageSize, setSentPageSize] = useState(1000);
  const [sentTotalPage, setSentTotalPage] = useState(0);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [receivedPage, setReceivedPage] = useState(1);
  const [receivedPageSize, setReceivedPageSize] = useState(1000);
  const [receivedTotalPage, setReceivedTotalPage] = useState(0);
  const [receivedTotalCount, setReceivedTotalCount] = useState(0);

  const [isUploadModalVisible, setIsUploadModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm<UploadFormValues>();
  const isPublic = Form.useWatch('is_public', form) ?? false;
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);

  const [sentFiles, setSentFiles] = useState<EnhancedFilehubFilesData[]>([]);
  const [receivedFiles, setReceivedFiles] = useState<EnhancedFilehubFilesData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assignedInvestors, setAssignedInvestors] = useState<InvestorProfile[]>([]);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [sortBy, setSortBy] = useState<'upload_at' | 'filename' | 'share_count'>('upload_at');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>('descend');
  // const [sentTablePaginationKey, setSentTablePaginationKey] = useState<
  //   string | undefined
  // >();
  // const [receivedTablePaginationKey, setReceivedTablePaginationKey] = useState<
  //   string | undefined
  // >();
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const [filteredSentFiles, setFilteredSentFiles] = useState<EnhancedFilehubFilesData[]>([]);
  const [filteredReceivedFiles, setFilteredReceivedFiles] = useState<EnhancedFilehubFilesData[]>(
    [],
  );

  const fetchAssignedInvestors = async () => {
    try {
      const investorsData: InvestorProfile[] = await getInvestors({
        limit: 1024,
        // advisor: me["id"], remove the advisor filter
        // so that the advisor can see all investors
      });
      setAssignedInvestors(investorsData);
    } catch (error) {
      console.error('Failed to fetch assigned investors:', error);
      message.error('Failed to load investor data');
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    if (info.fileList.length > 0) {
      const lastFile = info.fileList[info.fileList.length - 1];
      const rawFile = lastFile.originFileObj as RcFile | undefined;

      if (rawFile && !isFileWithinSizeLimit(rawFile)) {
        message.error('File size must be 100MB or smaller');
        setFileList([]);
        return;
      }

      setFileList([lastFile]);
    } else {
      setFileList([]);
    }
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (!isFileWithinSizeLimit(file)) {
      message.error('File size must be 100MB or smaller');
      setFileList([]);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handlePreview = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      window.open(downloadLinkResponse.preview_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to download shared file:', error);
      message.error('Failed to download file');
    }
  };

  const handleDownload = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      // The backend's download_url embeds the raw filename in the S3
      // response-content-disposition header, which S3 rejects with
      // InvalidArgument when the name contains non ISO-8859-1 characters
      // (e.g. Unicode spaces in macOS screenshots). That made every
      // non-plain-ASCII-named file fail to download. Fetch the object via the
      // preview_url (no content-disposition) and save it client-side so the
      // filename is applied locally and every file type downloads reliably.
      const response = await fetch(downloadLinkResponse.preview_url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = record.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Failed to download shared file:', error);
      message.error('Failed to download file');
    }
  };

  const handleDelete = useCallback(
    (record: FilehubFilesData) => {
      Modal.confirm({
        title: 'Delete Confirmation',
        content: `Are you sure you want to delete ${record.filename}?`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          try {
            await deleteAdvisorFile(record.key);

            setSentFiles((prevFiles) => prevFiles.filter((file) => file.key !== record.key));

            setFilteredSentFiles((prevFiles) => {
              const updatedFiles = prevFiles.filter((file) => file.key !== record.key);

              setSentTotalCount(updatedFiles.length);
              setSentTotalPage(Math.ceil(updatedFiles.length / sentPageSize));

              return updatedFiles;
            });
            message.success(`${record.filename} deleted successfully`);
          } catch (error) {
            console.error('Delete failed:', error);
            message.error(`Failed to delete ${record.filename}`);
          }
        },
      });
    },
    [sentPageSize],
  );

  const fetchAllSentFiles = async (
    searchKeyword: string = '',
  ): Promise<EnhancedFilehubFilesData[]> => {
    try {
      let allFiles: EnhancedFilehubFilesData[] = [];
      let hasMore = true;
      let paginationKey: string | undefined = undefined;

      while (hasMore) {
        const response = await getAdvisorFilesWithShares({
          limit: sentPageSize,
          pagination_key: paginationKey,
          // sort_by: sortBy,
          sort_order: sortOrder === 'ascend' ? 'asc' : 'desc',
          // search_keyword: searchKeyword,
          include_share_details: true,
        });

        const filesWithSource = Array.isArray(response.files)
          ? response.files.map((file) => ({ ...file, source: 'sent' as const }))
          : [];

        allFiles = [...allFiles, ...filesWithSource];

        paginationKey = response.pagination_key;
        hasMore = response.has_more;
      }

      return allFiles;
    } catch (error) {
      console.error('Error fetching all sent files:', error);
      setError('Failed to load all sent files');
      return [];
    }
  };

  const fetchAllReceivedFiles = async (
    searchKeyword: string = '',
  ): Promise<EnhancedFilehubFilesData[]> => {
    try {
      let allFiles: EnhancedFilehubFilesData[] = [];
      let hasMore = true;
      let paginationKey: string | undefined = undefined;
      let RIAhasMore = true;
      let RIApaginationKey: string | undefined = undefined;

      while (hasMore) {
        const response = await getReceivedAdvisorFilesWithShares({
          limit: receivedPageSize,
          pagination_key: paginationKey,
          // sort_by: sortBy,
          sort_order: sortOrder === 'ascend' ? 'asc' : 'desc',
          // search_keyword: searchKeyword,
          include_share_details: true,
        });

        const filesWithSource = Array.isArray(response.files)
          ? response.files.map((file) => ({
              ...file,
              source: 'received' as const,
            }))
          : [];

        allFiles = [...allFiles, ...filesWithSource];

        paginationKey = response.pagination_key;
        hasMore = response.has_more;
      }

      while (RIAhasMore) {
        const response = await getReceivedAdvisorFilesWithSharesByClientId({
          limit: receivedPageSize,
          pagination_key: RIApaginationKey,
          // sort_by: sortBy,
          sort_order: sortOrder === 'ascend' ? 'asc' : 'desc',
          // search_keyword: searchKeyword,
          include_share_details: true,
        });

        const filesWithSource = Array.isArray(response.files)
          ? response.files.map((file) => ({
              ...file,
              source: 'received' as const,
            }))
          : [];

        allFiles = [...allFiles, ...filesWithSource];

        RIApaginationKey = response.pagination_key;
        RIAhasMore = response.has_more;
      }

      return allFiles;
    } catch (error) {
      console.error('Error fetching all received files:', error);
      setError('Failed to load all received files');
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [allSentFiles, allReceivedFiles] = await Promise.all([
        fetchAllSentFiles(searchKeyword),
        fetchAllReceivedFiles(searchKeyword),
      ]);
      await fetchAssignedInvestors();

      setSentFiles(allSentFiles);
      setReceivedFiles(allReceivedFiles);

      setFilteredSentFiles(allSentFiles);
      setFilteredReceivedFiles(allReceivedFiles);

      setSentTotalCount(allSentFiles.length);
      setReceivedTotalCount(allReceivedFiles.length);

      setSentTotalPage(Math.ceil(allSentFiles.length / sentPageSize));
      setReceivedTotalPage(Math.ceil(allReceivedFiles.length / receivedPageSize));
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to load files data');
      setSentFiles([]);
      setReceivedFiles([]);
      setFilteredSentFiles([]);
      setFilteredReceivedFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        const keyword = value.toLowerCase();

        const filterFilesByKeyword = (files: EnhancedFilehubFilesData[]) =>
          keyword ? files.filter((file) => file.filename.toLowerCase().includes(keyword)) : files;

        const newFilteredSentFiles = filterFilesByKeyword(sentFiles);
        const newFilteredReceivedFiles = filterFilesByKeyword(receivedFiles);

        setFilteredSentFiles(newFilteredSentFiles);
        setFilteredReceivedFiles(newFilteredReceivedFiles);

        setSentPage(1);
        setReceivedPage(1);
        setSentTotalCount(newFilteredSentFiles.length);
        setReceivedTotalCount(newFilteredReceivedFiles.length);
        setSentTotalPage(Math.ceil(newFilteredSentFiles.length / sentPageSize));
        setReceivedTotalPage(Math.ceil(newFilteredReceivedFiles.length / receivedPageSize));
      }, 300);
    },
    [sentFiles, receivedFiles, sentPageSize, receivedPageSize],
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSentPaginationChange: PaginationProps['onChange'] = (page, pageSize) => {
    setSentPage(page);
    setSentPageSize(pageSize);

    setSentTotalPage(Math.ceil(sentFiles.length / pageSize));
  };

  const handleReceivedPaginationChange: PaginationProps['onChange'] = (page, pageSize) => {
    setReceivedPage(page);
    setReceivedPageSize(pageSize);

    setReceivedTotalPage(Math.ceil(receivedFiles.length / pageSize));
  };

  const investorFilesMap = useMemo(() => {
    const map = new Map<string, EnhancedFilehubFilesData[]>();

    assignedInvestors.forEach((investor) => {
      const investorIds = [investor.id, investor.cognito_id].filter(isPresentString);

      const sentFilesForUser =
        filteredSentFiles.length > 0
          ? filteredSentFiles.filter((file) => {
              return investorIds.some((investorId) => file.shares?.includes(investorId));
            })
          : [];

      const receivedFilesForUser =
        filteredReceivedFiles.length > 0
          ? filteredReceivedFiles.filter((file) => {
              return file.owner_cognito_id ? investorIds.includes(file.owner_cognito_id) : false;
            })
          : [];

      const userFiles = [...sentFilesForUser, ...receivedFilesForUser].sort(
        (a, b) => new Date(b.upload_at).getTime() - new Date(a.upload_at).getTime(),
      );

      if (userFiles.length > 0) {
        map.set(investor.id, userFiles);
      }
    });

    return map;
  }, [assignedInvestors, filteredSentFiles, filteredReceivedFiles]);

  const userFileColumns: TableProps<EnhancedFilehubFilesData>['columns'] = [
    {
      title: 'Title',
      dataIndex: 'filename',
      key: 'filename',
    },
    {
      title: 'Date',
      dataIndex: 'upload_at',
      key: 'upload_at',
      render: (upload_at: string) => dayjs(upload_at).format('MMM D, YYYY'),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source: 'sent' | 'received') => (
        <Tag color={source === 'sent' ? 'blue' : 'green'}>
          {source === 'sent' ? 'Sent' : 'Received'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record: EnhancedFilehubFilesData) => (
        <div>
          <Button type="link" onClick={() => handlePreview(record)}>
            Preview
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            aria-label={`Download ${record.filename}`}
            title={`Download ${record.filename}`}
            onClick={() => handleDownload(record)}
          />
          {/* Download
          </Button> */}
          {record.source === 'sent' && (
            <Button
              type="link"
              icon={<DeleteOutlined />}
              aria-label={`Delete ${record.filename}`}
              title={`Delete ${record.filename}`}
              danger
              onClick={() => handleDelete(record)}
            />
            //   Delete
            // </Button>
          )}
        </div>
      ),
    },
  ];

  const getPaginatedData = (files: EnhancedFilehubFilesData[], page: number, size: number) => {
    return files.slice((page - 1) * size, page * size);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    // setCurrentPage(1);
  };

  const columns: TableProps<EnhancedFilehubFilesData>['columns'] = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      // sorter: true,
      // sortOrder: sortBy === 'filename' ? sortOrder : null,
    },
    {
      title: 'Size',
      dataIndex: 'size_byte',
      key: 'size_byte',
      render: (size_byte: number) => formatFileSize(size_byte),
    },
    {
      title: 'Upload date',
      dataIndex: 'upload_at',
      key: 'upload_at',
      // sorter: true,
      // sortOrder: sortBy === 'upload_at' ? sortOrder : null,
      render: (upload_at: string) => dayjs(upload_at).format('MMM D, YYYY'),
    },
    ...(activeTab === 'all'
      ? [
          {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            render: (source: 'sent' | 'received') => (
              <Tag color={source === 'sent' ? 'blue' : 'green'}>
                {source === 'sent' ? 'Sent' : 'Received'}
              </Tag>
            ),
          },
        ]
      : []),
    {
      title: '',
      key: 'actions',
      render: (_, record: EnhancedFilehubFilesData) => (
        <div>
          <Button
            type="link"
            // icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            Preview
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            aria-label={`Download ${record.filename}`}
            title={`Download ${record.filename}`}
            onClick={() => handleDownload(record)}
          />
          {/* Download
          </Button> */}
          {(activeTab === 'sent' || (activeTab === 'all' && record.source === 'sent')) && (
            <Button
              type="link"
              icon={<DeleteOutlined />}
              aria-label={`Delete ${record.filename}`}
              title={`Delete ${record.filename}`}
              danger
              onClick={() => handleDelete(record)}
            />
            //   Delete
            // </Button>
          )}
        </div>
      ),
    },
  ];

  const handleIsPublicChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    if (checked) {
      const allInvestorIds = assignedInvestors.map((investor) => investor.id);
      form.setFieldsValue({
        shares: allInvestorIds,
      });
    } else {
      form.setFieldsValue({ shares: [] });
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'all',
      label: 'Investor Documents',
      children:
        investorFilesMap.size === 0 ? (
          <Empty
            description={
              searchKeyword
                ? `No documents found matching "${searchKeyword}"`
                : 'No documents found for any investor'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Collapse>
            {Array.from(investorFilesMap.entries()).map(([investorId, userFiles]) => {
              const investor = assignedInvestors.find((candidate) => candidate.id === investorId);

              if (!investor) return null;

              return (
                <Collapse.Panel key={investorId} header={investor.name}>
                  <Table
                    columns={userFileColumns}
                    dataSource={userFiles.map((file) => ({
                      ...file,
                      key: file.key,
                    }))}
                    pagination={false}
                  />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        ),
    },
    {
      key: 'received',
      label: 'Shared with me',
      children: (
        <Table
          columns={columns}
          dataSource={getPaginatedData(filteredReceivedFiles, receivedPage, receivedPageSize).map(
            (file) => ({ ...file, key: file.key }),
          )}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={
                  searchKeyword
                    ? `No received files matching "${searchKeyword}"`
                    : 'No files shared with you yet'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          footer={() => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row-reverse',
                alignItems: 'center',
              }}
            >
              <div>
                <Pagination
                  current={receivedPage}
                  pageSize={receivedPageSize}
                  total={receivedTotalCount}
                  onChange={handleReceivedPaginationChange}
                  showTotal={() => `Total ${receivedTotalCount} items`}
                />
              </div>
            </div>
          )}
          // onChange={handleSort}
          loading={loading}
        />
      ),
    },
    {
      key: 'sent',
      label: 'Shared by me',
      children: (
        <Table
          columns={columns}
          dataSource={getPaginatedData(filteredSentFiles, sentPage, sentPageSize).map((file) => ({
            ...file,
            key: file.key,
          }))}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={
                  searchKeyword
                    ? `No sent files matching "${searchKeyword}"`
                    : "You haven't shared any files yet"
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          footer={() => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row-reverse',
                alignItems: 'center',
              }}
            >
              <div>
                <Pagination
                  current={sentPage}
                  pageSize={sentPageSize}
                  total={sentTotalCount}
                  onChange={handleSentPaginationChange}
                  showTotal={() => `Total ${sentTotalCount} items`}
                />
              </div>
            </div>
          )}
          // onChange={handleSort}
          loading={loading}
        />
      ),
    },
  ];

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a file to upload');
      return;
    }

    const file = fileList[0].originFileObj as RcFile;
    if (!file) return;

    if (!isFileWithinSizeLimit(file)) {
      message.error('File size must be 100MB or smaller');
      setFileList([]);
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();

      const presignedUrlResponse: getFilehubPresignedURLResponse = await getFilehubPresignedURL(
        {
          is_public: isPublic,
          requires_document_processing: false,
          shares: form.getFieldValue('shares'),
          file_name: file.name,
          file_size: file.size,
          file_type: getFileType(file),
        },
        file,
      );

      Object.entries(presignedUrlResponse.presigned_body.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', presignedUrlResponse.presigned_body.url, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const shares = form.getFieldValue('shares');

            const response = await confirmUploadFile(
              presignedUrlResponse.data.key,
              file.name,
              presignedUrlResponse.data.owner_cognito_id,
              Array.isArray(shares) ? shares : shares.split(','),
            );
            // setSentFiles((prev) => [...prev, response.file]);
            const newFile = { ...response.file, source: 'sent' as const };

            setSentFiles((prev) => [newFile, ...prev]);

            const shouldIncludeInFiltered =
              !searchKeyword ||
              newFile.filename.toLowerCase().includes(searchKeyword.toLowerCase());

            if (shouldIncludeInFiltered) {
              setFilteredSentFiles((prev) => {
                const updated = [newFile, ...prev];
                setSentTotalCount(updated.length);
                setSentTotalPage(Math.ceil(updated.length / sentPageSize));
                return updated;
              });
            }

            message.success(`${file.name} uploaded successfully`);
            setIsUploadModalVisible(false);
            form.resetFields();
            setFileList([]);
            setUploadProgress(0);
          } catch (error) {
            console.error('Failed to confirm upload:', error);
            message.error(`Failed to upload ${file.name}`);
          }
        } else {
          // onError(new Error(`Upload failed with status ${xhr.status}`));
          message.error(`Failed to upload ${file.name}`);
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        // onError(new Error("Network error"));
        message.error(`Network error while uploading ${file.name}`);
        setUploading(false);
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Upload failed');
      setUploading(false);
    }
  };

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div className="file_hub">
      <div className="table">
        <div className="search-bar">
          <Input
            className="search-input"
            placeholder="Search files..."
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Spin spinning={loading} tip="Loading data...">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
            className="file-tabs"
          />
        </Spin>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => setIsUploadModalVisible(true)}
          className="upload-button"
        >
          Upload files
        </Button>
      </div>

      <Modal
        className="upload-modal"
        title="Secure Upload"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setFileList([]);
          form.resetFields();
        }}
        maskClosable={false}
        width={600}
        footer={null}
      >
        <div className="upload-area">
          <p className="text">Use this area to securely upload documents to your investors.</p>
          <Upload.Dragger
            fileList={fileList}
            beforeUpload={handleBeforeUpload}
            onChange={handleChange}
            multiple={false}
            showUploadList={true}
            customRequest={({ onSuccess = () => {} }) => {
              onSuccess('ok');
            }}
          >
            <p className="ant-upload-drag-icon">
              <img src={File} alt="Upload" />
            </p>
            <p className="ant-upload-text">Drag files here or click to upload (max size: 100MB)</p>
          </Upload.Dragger>
        </div>

        <Form
          className="modal-form"
          form={form}
          layout="vertical"
          initialValues={{ is_public: false, shares: [] }}
        >
          <Form.Item name="shares" label="Share With">
            <Select
              mode="multiple"
              placeholder="Select investors to share with"
              style={{ width: '100%' }}
              optionFilterProp="label"
              disabled={isPublic}
            >
              {assignedInvestors.map((investor) => (
                <Select.Option key={investor.id} value={investor.id} label={investor.name}>
                  <div className="select-option">
                    <span className="select-option-name">{investor.name}</span>
                    <span className="select-option-email">{investor.email}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_public" valuePropName="checked">
            <Checkbox onChange={handleIsPublicChange}>Share with all investors</Checkbox>
          </Form.Item>

          {uploading && (
            <Form.Item>
              <Progress percent={uploadProgress} status="active" />
            </Form.Item>
          )}

          <Form.Item className="modal-form-footer">
            <Space>
              <Button
                onClick={() => {
                  setIsUploadModalVisible(false);
                  setFileList([]);
                  form.resetFields();
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                disabled={fileList.length === 0}
              >
                Upload File
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FileHub;
